import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Transition } from 'react-transition-group';
import {
  setChatInterface,
  selectChatInterfaceState,
  isTwilioConnected,
  isTwilioFullscreen,
} from 'models/twilio';
import { selectEnrichmentDataShow } from 'models/enrichmentData';
import useEnableExhibitorPanelTreatment from 'hooks/useEnableExhibitorPanelTreatment';
import DesktopChatHeader from './DesktopChatHeader';
import ConversationList from '../ConversationList';
import {
  BEHAVIOR, COLORS, POSITIONING, scrollBarStyles,
} from '../../constants';
import ChatInterface from '../ChatInterface';
import ChatDivider from '../styled/ChatDivider';
import ChatSnackbar from '../ChatSnackbar';
import TwilioConnectedWrapper from '../TwilioConnectedWrapper';

interface DesktopContainerProps {
  $chatInterfaceActive: boolean;
  $fullscreen: boolean;
  $exhibitorPanel?: boolean;
  $showDataEnrichment?: boolean;
}

const RootContainer = styled.div<DesktopContainerProps>`
  position: absolute;
  right: ${({ $chatInterfaceActive, $fullscreen, $showDataEnrichment }) => {
    if ($showDataEnrichment) return 315;
    if (!$chatInterfaceActive) return 20;
    return $fullscreen ? 0 : 20;
  }}px;
  bottom: 0;
  z-index: 1100;
  overflow: hidden;
  transition: right 0.4s;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);
  border-radius: 4px 4px 0 0;
`;

const DesktopChatOverlayContainer = styled.div<DesktopContainerProps>`
  height: ${({ $chatInterfaceActive, $fullscreen }) => {
    if (!$chatInterfaceActive) return POSITIONING.HEADER_HEIGHT;
    return $fullscreen ? POSITIONING.FULLSCREEN.INTERFACE_HEIGHT : POSITIONING.DESKTOP.INTERFACE_HEIGHT;
  }};
  max-height: calc(100vh - 64px);
  width: ${({ $chatInterfaceActive, $fullscreen, $exhibitorPanel }) => {
    if (!$chatInterfaceActive) return $exhibitorPanel ? POSITIONING.DESKTOP.PANEL_BUTTON_WIDTH : POSITIONING.DESKTOP.CHAT_BUTTON_WIDTH;
    return $fullscreen ? POSITIONING.FULLSCREEN.INTERFACE_WIDTH : POSITIONING.DESKTOP.INTERFACE_WIDTH;
  }};
  position: relative;
  transition: all 0.5s;
  background-color: ${COLORS.WHITE};
`;

const DesktopOverlayContentContainer = styled(TwilioConnectedWrapper)`
  display: flex;
  width: 100%;
  overflow: hidden;
  height: calc(100% - ${POSITIONING.HEADER_HEIGHT});
  position: absolute;
  top: ${POSITIONING.HEADER_HEIGHT};
`;

const DesktopConversationsContainer = styled.div`
  background-color: ${COLORS.WHITE};
  width: ${POSITIONING.DESKTOP.LIST_WIDTH};
  max-width: 500px;
  overflow-y: auto;
  ${scrollBarStyles}
`;

const DesktopChatContainer = styled.div`
  height: 100%;
  width: ${POSITIONING.DESKTOP.CHAT_WIDTH};
  display: flex;
  flex-grow: 1;
`;

const DesktopChatOverlay = () => {
  const dispatch = useDispatch();
  const isFullscreen = useSelector(isTwilioFullscreen);
  const chatInterfaceActive = useSelector(selectChatInterfaceState);
  const twilioDisconnected = !useSelector(isTwilioConnected);
  const showDataEnrichment = useSelector(selectEnrichmentDataShow);
  const showExhibitorPanel = useEnableExhibitorPanelTreatment();

  const displayChat = useCallback(() => {
    if (twilioDisconnected) return;
    dispatch(setChatInterface(true));
  }, [dispatch, twilioDisconnected]);

  return (
    <RootContainer
      $chatInterfaceActive={chatInterfaceActive}
      $fullscreen={isFullscreen}
      $showDataEnrichment={showDataEnrichment}
    >
      <DesktopChatOverlayContainer
        id="desktop-chat-overlay-container"
        $chatInterfaceActive={chatInterfaceActive}
        $exhibitorPanel={showExhibitorPanel}
        $fullscreen={isFullscreen}
        onClick={displayChat}
      >
        <DesktopChatHeader />
        <Transition in={chatInterfaceActive} timeout={BEHAVIOR.CHAT_ANIMATION_TIMEOUT} unmountOnExit mountOnEnter>
          {(state) => (state !== 'exited' ? (
            <DesktopOverlayContentContainer id="desktop-overlay-content-container">
              <DesktopConversationsContainer id="desktop-overlay-conversation-list">
                <ConversationList />
              </DesktopConversationsContainer>
              <ChatDivider orientation="vertical" flexItem />
              <DesktopChatContainer id="desktop-chat-interface-container">
                <ChatInterface />
              </DesktopChatContainer>
              <ChatSnackbar />
            </DesktopOverlayContentContainer>
          ) : (
            null
          ))}
        </Transition>
      </DesktopChatOverlayContainer>
    </RootContainer>
  );
};

export default DesktopChatOverlay;
